import { useRef, useState } from 'react'
import {
	coverWindow,
	safeZoneImageStyle,
	type SafeZone,
} from '#app/domains/program/pure'

export type SafeZoneFrame = { label: string; aspect: number }

type Rect = Pick<SafeZone, 'x' | 'y' | 'w' | 'h'>
type Drag =
	| { kind: 'draw'; startX: number; startY: number }
	| { kind: 'move'; dx: number; dy: number; w: number; h: number }
	| { kind: 'resize'; anchorX: number; anchorY: number }

const MIN = 0.08
const clamp = (n: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, n))

/**
 * Mark the part of a photo that must never be cropped away (faces,
 * instruments). The photo is cropped differently on cards, the concert page
 * and the poster; the previews show what each of them will keep.
 */
export function SafeZoneEditor({
	src,
	value,
	frames,
	onChange,
}: {
	src: string
	value: SafeZone | null
	frames: SafeZoneFrame[]
	onChange: (zone: SafeZone | null) => void
}) {
	const [ar, setAr] = useState<number | null>(null)
	const boxRef = useRef<HTMLDivElement>(null)
	const drag = useRef<Drag | null>(null)

	const point = (e: React.PointerEvent) => {
		const box = boxRef.current!.getBoundingClientRect()
		return {
			x: clamp((e.clientX - box.left) / box.width),
			y: clamp((e.clientY - box.top) / box.height),
		}
	}
	const emit = (rect: Rect) => ar && onChange({ ...rect, ar })

	const onDown = (e: React.PointerEvent, kind?: 'move' | 'resize') => {
		if (!ar) return
		e.preventDefault()
		e.stopPropagation()
		boxRef.current!.setPointerCapture(e.pointerId)
		const p = point(e)
		if (kind === 'move' && value) {
			drag.current = {
				kind: 'move',
				dx: p.x - value.x,
				dy: p.y - value.y,
				w: value.w,
				h: value.h,
			}
		} else if (kind === 'resize' && value) {
			// grab a corner: the opposite one stays put
			const anchorX = p.x > value.x + value.w / 2 ? value.x : value.x + value.w
			const anchorY = p.y > value.y + value.h / 2 ? value.y : value.y + value.h
			drag.current = { kind: 'resize', anchorX, anchorY }
		} else {
			drag.current = { kind: 'draw', startX: p.x, startY: p.y }
		}
	}

	const onMove = (e: React.PointerEvent) => {
		const d = drag.current
		if (!d) return
		const p = point(e)
		if (d.kind === 'move') {
			emit({
				x: clamp(p.x - d.dx, 0, 1 - d.w),
				y: clamp(p.y - d.dy, 0, 1 - d.h),
				w: d.w,
				h: d.h,
			})
			return
		}
		const [ax, ay] =
			d.kind === 'draw' ? [d.startX, d.startY] : [d.anchorX, d.anchorY]
		emit({
			x: Math.min(ax, p.x),
			y: Math.min(ay, p.y),
			w: Math.abs(p.x - ax),
			h: Math.abs(p.y - ay),
		})
	}

	const onUp = () => {
		drag.current = null
		// a click without a drag leaves a sliver: drop it
		if (value && (value.w < MIN || value.h < MIN)) onChange(null)
	}

	return (
		<div className="flex flex-col gap-3">
			<p className="text-base-content/70 text-sm">
				Dra over biletet for å markere det som aldri må skjerast bort (andlet,
				instrument). Flytt eller dra i hjørna for å justere.
			</p>
			<div
				ref={boxRef}
				className="bg-base-200 relative mx-auto w-full max-w-md touch-none overflow-hidden rounded-xl select-none"
				onPointerDown={(e) => onDown(e)}
				onPointerMove={onMove}
				onPointerUp={onUp}
				onPointerCancel={onUp}
			>
				<img
					src={src}
					alt="Marker trygg sone"
					draggable={false}
					className="block w-full"
					onLoad={(e) =>
						setAr(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)
					}
				/>
				{value ? (
					<div
						data-testid="safe-zone-rect"
						className="absolute cursor-move border-2 border-white"
						style={{
							left: `${value.x * 100}%`,
							top: `${value.y * 100}%`,
							width: `${value.w * 100}%`,
							height: `${value.h * 100}%`,
							boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
						}}
						onPointerDown={(e) => {
							const p = point(e)
							const nearX =
								Math.abs(p.x - value.x) < 0.05 ||
								Math.abs(p.x - (value.x + value.w)) < 0.05
							const nearY =
								Math.abs(p.y - value.y) < 0.05 ||
								Math.abs(p.y - (value.y + value.h)) < 0.05
							onDown(e, nearX && nearY ? 'resize' : 'move')
						}}
					/>
				) : null}
			</div>

			<div className="grid grid-cols-3 items-end gap-3">
				{frames.map((frame) => {
					const fits = coverWindow(value, frame.aspect).fits
					return (
						<figure key={frame.label} className="flex flex-col gap-1">
							<div
								className="bg-base-200 overflow-hidden rounded-lg"
								style={{ aspectRatio: String(frame.aspect) }}
							>
								<img
									src={src}
									alt=""
									className="h-full w-full object-cover"
									style={safeZoneImageStyle(value, frame.aspect)}
								/>
							</div>
							<figcaption className="text-xs">
								{frame.label}
								{fits ? null : (
									<span className="text-warning"> · sona passar ikkje</span>
								)}
							</figcaption>
						</figure>
					)
				})}
			</div>
			{value ? (
				<button
					type="button"
					className="btn btn-ghost btn-sm self-start"
					onClick={() => onChange(null)}
				>
					Nullstill sone
				</button>
			) : null}
		</div>
	)
}
